import { useEffect, useMemo, useState } from "react";
import { SidePanel, Typography } from "@/components/atoms";
import { useLazyGetBookingDetailsQuery } from "@/redux/apis/booking/bookingApi";
import { transformBookingDetails } from "@/redux/apis/booking/helpers";
import { useAppSelector } from "@/redux/hooks";
import { bookingsPageStateSelector } from "@/redux/slices/booking/selectors";
import { BookingActions } from "../../BookingActions";
import { BookingInlineActions } from "../../BookingInlineActions";
import { DriverActionsSection } from "../../shared/sections/DriverActionsSection";
import { PaymentSection } from "../../shared/sections/PaymentSection";
import { TripSection } from "../../shared/sections/TripSection";
import { BookingPanelDetailsHeaderSkeleton, BookingPanelDetailsBodySkeleton } from "./BookingPanelDetailsSkeleton";
import { BookingActionsBar } from "./components/BookingActionsBar";
import { BookingHeader } from "./components/BookingHeader";
import { AttachmentsSection } from "./sections/AttachmentsSection";
import { BasicDetailsSection } from "./sections/BasicDetailsSection";
import { DistanceSection } from "./sections/DistanceSection";
import { DriverSection } from "./sections/DriverSection";
import { ExtrasFeesSection } from "./sections/ExtrasFeesSection";
import { FlightSection } from "./sections/FlightSection";
import { HistorySection } from "./sections/HistorySection";
import { MessageSection } from "./sections/MessageSection";
import { NotesSection } from "./sections/NotesSection";
import { OperatorSection } from "./sections/OperatorSection";
import { PassengerSection } from "./sections/PassengerSection";
import { VehicleSection } from "./sections/VehicleSection";

interface BookingPanelDetailsProps {
  id?: string;
  open: boolean;
  onClose: () => void;
}

export const BookingPanelDetails = ({ id, open, onClose }: BookingPanelDetailsProps) => {
  const [fetchBookingDetails, { data: rawBookingDetails, isLoading, isFetching, error }] = useLazyGetBookingDetailsQuery();
  const { inlineEditing, actionsState } = useAppSelector(bookingsPageStateSelector);
  const [flightModalOpen, setFlightModalOpen] = useState(false);

  const bookingDetails = useMemo(() => {
    if (!rawBookingDetails) return null;

    const transformedBooking = transformBookingDetails(rawBookingDetails);
    return transformedBooking;
  }, [rawBookingDetails]);

  useEffect(() => {
    if (id && open) {
      fetchBookingDetails(id);
    }
  }, [id, open, fetchBookingDetails]);

  const hasNestedModalsOpen = Boolean(inlineEditing) || Boolean(actionsState.action) || flightModalOpen;

  const handleExplicitClose = () => {
    if (hasNestedModalsOpen) {
      return;
    }
    onClose();
  };

  if (!bookingDetails || isLoading || isFetching || error) {
    return (
      <SidePanel open={open} onClose={onClose} width={786} focused={false}>
        {isLoading || isFetching ? (
          [<BookingPanelDetailsHeaderSkeleton key="header" />, <BookingPanelDetailsBodySkeleton key="body" />]
        ) : (
          <div className="p-5">
            {error && <Typography className="text-danger">Error loading booking details</Typography>}
            {!id && <Typography className="text-danger">No booking selected</Typography>}
          </div>
        )}
      </SidePanel>
    );
  }

  return (
    <>
      <SidePanel open={open} onClose={handleExplicitClose} width={786} focused={false}>
        <BookingHeader booking={bookingDetails} onClose={handleExplicitClose} />

        <div>
          <BookingActionsBar booking={bookingDetails} />

          <div className="grid grid-cols-1 gap-2 p-5 pt-0 lg:grid-cols-2">
            <div className="space-y-2.5">
              <BasicDetailsSection booking={bookingDetails} />
              <TripSection booking={bookingDetails} />
              {/* DSN-689: the flight belongs to the airport pickup, so it sits right under the address. */}
              <FlightSection booking={bookingDetails} onModalStateChange={setFlightModalOpen} />
              <PassengerSection booking={bookingDetails} />
              <ExtrasFeesSection booking={bookingDetails} />
              <DriverActionsSection booking={bookingDetails} />
              <PaymentSection booking={bookingDetails} />
              <OperatorSection booking={bookingDetails} />
            </div>

            <div className="space-y-2.5">
              <DriverSection booking={bookingDetails} />
              <VehicleSection booking={bookingDetails} />
              <NotesSection booking={bookingDetails} />
              <MessageSection booking={bookingDetails} />
              <AttachmentsSection booking={bookingDetails} />
              <HistorySection booking={bookingDetails} />
              <DistanceSection booking={bookingDetails} />
            </div>
          </div>
        </div>
      </SidePanel>

      <BookingInlineActions />
      <BookingActions />
    </>
  );
};
